/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { FolderKanban } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import {
  FlexCell,
  ListRow,
  Num,
  Pill,
  Primary,
  Secondary,
  Stack,
  T9,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

import type { Project } from "@src/@types/Project";

export const PROJECT_COLUMNS: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(260px, 2.4fr)" },
  { key: "members", label: "Members", width: "96px", align: "right" },
  { key: "status", label: "Status", width: "112px" },
  { key: "current", label: "Your session", width: "150px" },
];

const SwitchButton = styled.button`
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  color: ${T9.slate700};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${T9.slate50};
    color: ${T9.slate900};
  }
  &:disabled {
    color: ${T9.slate400};
    border-color: ${T9.slate200};
    cursor: not-allowed;
  }
`;

type Props = {
  item: Project;
  onClick: () => void;
  getMembers: (projectId: string) => number;
  isCurrentProject: (projectId: string) => boolean;
  onSwitchProjectClick: (projectId: string) => void | Promise<void>;
  selected?: boolean;
  selectable?: boolean;
  onSelectedChange?: (value: boolean) => void;
  actions?: DropdownAction[];
};
@observer
class ProjectListItem extends React.Component<Props> {
  render() {
    const { item } = this.props;
    const isCurrentProject = this.props.isCurrentProject(item.id);

    return (
      <ListRow
        columns={PROJECT_COLUMNS}
        selectable={this.props.selectable}
        selected={this.props.selected}
        onSelectedChange={this.props.onSelectedChange}
        onClick={this.props.onClick}
        actions={this.props.actions}
        testId="projectListItem"
      >
        <FlexCell gap={12}>
          <IconTile icon={FolderKanban} size={28} />
          <Stack>
            <Primary title={item.name}>{item.name}</Primary>
            {item.description ? <Secondary title={item.description}>{item.description}</Secondary> : null}
          </Stack>
        </FlexCell>
        <Num>{this.props.getMembers(item.id)}</Num>
        <div>
          {item.enabled === false ? <Pill tone="neutral">Disabled</Pill> : <Pill tone="success">Enabled</Pill>}
        </div>
        <div
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {isCurrentProject ? (
            <Pill tone="brand">Current project</Pill>
          ) : (
            <SwitchButton
              type="button"
              disabled={item.enabled === false}
              title={item.enabled === false ? "The project is disabled" : undefined}
              onClick={() => {
                this.props.onSwitchProjectClick(item.id);
              }}
            >
              Switch to project
            </SwitchButton>
          )}
        </div>
      </ListRow>
    );
  }
}

export default ProjectListItem;
